import React from 'react';

import {
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  Stack,
  styled,
  Typography
} from '@mui/material';
import {
  AuthWrapper,
  BaseOnboardingTemplate,
  enumCache,
  ExternalLink,
  fields,
  InvestmentAreaMultiSelectField,
  InvestmentCategorySelectField,
  InvestmentGeographyGroupedMultiSelectField,
  InvestmentStageMultiSelectField,
  LoadingOverlay,
  ProfilePictureInputField,
  RequiredFieldLabel,
  routeDirectory,
  USStateSelectField
} from 'modules';

import { useNavigate } from 'react-router-dom';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import * as dataService from './dataService';



const ControlledTextField = fields.genTextFieldControllerComponent<dataService.IFormData>();
const InvestmentCategorySelectFieldController = fields.genEnumSelectFieldControllerComponent<dataService.IFormData, enumCache.IInvestmentCategory>(InvestmentCategorySelectField({
  label: 'Category'
}));
const InvestmentStageMultiSelectFieldController = fields.genEnumMultiSelectFieldControllerComponent<dataService.IFormData, enumCache.IInvestmentStage>(InvestmentStageMultiSelectField({
  label: 'Company/Investment Stage'
}));
const InvestmentGeographyGroupedMultiSelectFieldController = fields.genGroupedEnumMultiSelectFieldControllerComponent<dataService.IFormData, enumCache.IInvestmentGeography>(InvestmentGeographyGroupedMultiSelectField({
  label: 'Investment Geography (for investors only)'
}));
const InvestmentAreaMultiSelectFieldController = fields.genEnumMultiSelectFieldControllerComponent<dataService.IFormData, string>(InvestmentAreaMultiSelectField({
  label: 'Company/Investment Area'
}));
const USStateSelectFieldController = fields.genEnumSelectFieldControllerComponent<dataService.IFormData, string>(USStateSelectField({
  label: 'State'
}));
const CanLeadInvestmentsSelectFieldController = fields.genBasicSelectFieldControllerComponent<dataService.IFormData, dataService.CanLeadInvestmentsOptionNames>([['yes', 'Yes'], ['no', 'No']]);



const MoneyIcon = styled(AttachMoneyIcon)({
  color: '#528898'
});

const RoundPadBox = styled(Box)({
  width: 56,
  height: 56,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#C3D1D87F',
  borderRadius: 28
});

const RowGrid = styled(Grid)({
  display: 'flex',
  alignItems: 'center'
});

const HorizontalBar = styled(Box)({
  height: '2px',
  width: '23px',
  backgroundColor: '#C3D1D8',
  borderRadius: '1px'
});

const Caption = styled(Typography)({
  opacity: 0.72,
  textAlign: 'center',
  wrap: 'nowrap',
  letterSpacing: 0.4,
  marginTop: 4,
  fontFamily: 'Roboto',
  color: '#486c82',
  fontSize: 12,
  width: 169
});



export const OnboardingProfileActivity: React.FC = () => {
  const navigate = useNavigate();
  const { form, loading, data, mutations } = dataService.useProfileActivity();

  const [termsAndServicesChecked, setTermsAndServicesChecked] = React.useState(false);



  async function processSubmit(formData: dataService.IFormData) {
    try {
      await mutations.saveSiteUser(formData);
      if (data.hasAccessPass) {
        navigate(routeDirectory.home.path());
      } else {
        navigate(routeDirectory.authentication.onboardingPayment.path());
      }

    } catch (e) {
      // TODO: Handle Amplify errors
      alert(e);
    }
  }


  const watch = form.watch('profilePictureFile');

  return (
    <BaseOnboardingTemplate.FC
      stepperMode="profile"
      title="Profile"
      subtitle="Next, let's set up your profile. Investor profiles are visible to users and can be edited at any time.">

      <LoadingOverlay active={loading} />

      <form onSubmit={form.handleSubmit(processSubmit)}>
        <Grid
          container
          rowSpacing={4}
          spacing={3}>
          {/* Spacer for profile pic */}
          <Grid item xs={4} />

          <Grid item xs={4}>
            <ProfilePictureInputField.FC
              value={watch ?? undefined}
              initValue={data.profilePictureUrl}
              onChange={fileName => {
                form.setValue('profilePictureFile', fileName);
              }} />
            <Caption>Your Company Logo</Caption>
          </Grid>

          <Grid item xs={12}>
            <ControlledTextField
              formControl={form.control}
              isRequired
              label={(
                <RequiredFieldLabel.FC
                  text='Company Name'
                  isDisabled={loading} />
              )}
              fieldKey="companyName"
              isDisabled={loading} />
          </Grid>

          <Grid item xs={6}>
            <ControlledTextField
              formControl={form.control}
              label="Website"
              fieldKey="websiteUrl"
              isDisabled={loading} />
          </Grid>

          <Grid item xs={6}>
            <ControlledTextField
              formControl={form.control}
              label="Phone Number"
              fieldKey="phoneNumber"
              isDisabled={loading} />
          </Grid>

          <Grid item xs={6}>
            <InvestmentCategorySelectFieldController
              formControl={form.control}
              fieldKey="investmentCategory" />
          </Grid>

          <Grid item xs={6}>
            <InvestmentAreaMultiSelectFieldController
              formControl={form.control}
              fieldKey="investmentAreas" />
          </Grid>

          <Grid item xs={6}>
            <InvestmentStageMultiSelectFieldController
              formControl={form.control}
              fieldKey="investmentStages" />
          </Grid>

          <Grid item xs={6}>
            <InvestmentGeographyGroupedMultiSelectFieldController
              formControl={form.control}
              fieldKey="investmentGeographies"
              isDisabled={!data.isInvestor} />
          </Grid>

          <Grid item xs={12}>
            <ControlledTextField
              numLines={6}
              label="Company Description"
              fieldKey="companyDescription"
              formControl={form.control}
              isDisabled={loading} />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <Typography variant='h4'>Enter your check size range. (for investors only)</Typography>
          </Grid>

          <RowGrid item xs={12} justifyContent='space-between'>
            <Stack direction='row' alignItems='center' spacing={2}>
              <RoundPadBox>
                <MoneyIcon/>
              </RoundPadBox>

              <Box>
                <ControlledTextField
                  type='number'
                  label="Low"
                  fieldKey="checkSizeMinDollars"
                  formControl={form.control}
                  isDisabled={!data.isInvestor || loading} />
              </Box>
            </Stack>

            <HorizontalBar />

            <Stack direction='row' alignItems='center' spacing={2}>
              <RoundPadBox>
                <MoneyIcon />
              </RoundPadBox>

              <Box>
                <ControlledTextField
                  type='number'
                  label="High"
                  fieldKey="checkSizeMaxDollars"
                  formControl={form.control}
                  isDisabled={!data.isInvestor || loading} />
              </Box>
            </Stack>
          </RowGrid>

          <Grid item xs={12}>
            <CanLeadInvestmentsSelectFieldController
              fieldKey='canLeadInvestments'
              formControl={form.control}
              label='Leads Investments (for investors only)'
              isDisabled={!data.isInvestor || loading} />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={8}>
            <ControlledTextField
              formControl={form.control}
              isRequired
              label={(
                <RequiredFieldLabel.FC
                  text='City'
                  isDisabled={loading} />
              )}
              fieldKey="city"
              isDisabled={loading} />
          </Grid>

          <Grid item xs={2}>
            <USStateSelectFieldController
              formControl={form.control}
              fieldKey="usState"
              isDisabled={loading} />
          </Grid>

          <Grid item xs={2}>
            <ControlledTextField
              formControl={form.control}
              isRequired
              label={(
                <RequiredFieldLabel.FC
                  text='Country'
                  isDisabled={loading} />
              )}
              fieldKey="country"
              isDisabled={loading} />
          </Grid>

          <RowGrid item xs={12}>
            <Checkbox
              sx={{ color: '#87ACB9' }}
              onChange={(_evt, checked) => setTermsAndServicesChecked(checked)} />

            <Typography variant='h5'>
              By checking this box, you agree to all of Ridge's&nbsp;
              <ExternalLink.Text
                href="https://app.termly.io/document/terms-of-use-for-saas/a3dcd7e8-0fda-49db-9713-d1040682156e"
                color="#528898">
                terms and services
              </ExternalLink.Text>
              .
            </Typography>
          </RowGrid>

          <Grid item xs={12}>
            <Stack direction='row' justifyContent='space-between'>
              <Button
                color="secondary"
                variant="outlined"
                disabled={loading}
                onClick={() => navigate(-1)}>
                Back
              </Button>

              <Button
                type="submit"
                disabled={loading || !termsAndServicesChecked}>
                Next
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </BaseOnboardingTemplate.FC>
  );
};
